/**
 * @fileoverview Many photos in, one at a time underneath.
 *
 * Choose photos opens the same three ways in as a plant's Add a photo: the
 * camera, the device, Google Photos. The device's chooser and Google Photos
 * both take several at once. Each file is shrunk and sent on its own
 * (lib/useTrayBatch, shared with the add pop-ups' photo row since D78): a
 * dropped connection loses one picture rather than the batch, and the count
 * can say how far it has got. The bed the batch came from is the page's,
 * chosen once on the count line, so the question is not repeated on every pile.
 *
 * An iPhone photo (HEIC) is made a JPEG on the way, its date read from the
 * original. One that cannot be converted is not sent, since the server could
 * not read it, and counts among the ones that failed.
 *
 * Today's start card (its photo door) no longer hands photos over to this page: since
 * D78 they are reviewed in "Check and save" over Today.
 *
 * Where: PhotoTray's top row, beside "Photos from".
 * API: POST /tray, one request per photo, through useTrayBatch; Google Photos'
 * own picker (useGooglePhotos). Each photo that lands has the tray read
 * again (GET /tray, trayPhotoLanded), so it shows in the pile the server
 * puts it in as it lands.
 * Settings: TRAY_UPLOAD_CONCURRENCY (config/tunables/garden), through useTrayBatch.
 * An exact copy of a photo already added (the server answers duplicate_of, B61) is not stored: the batch ends with
 * "Already added, N copies skipped" and Undo (lib/photoCopies, through useTrayBatch), and the copy is not counted as
 * failed. A Google Photos pick sends Google's item id with the photo.
 * Words (en/es): tray.choose, tray.uploading, tray.uploadFailed,
 * tray.addTitle, photoCopies.*; the photo source picker's own words. An upload that failed
 * on our side (a 500, no connection; D31, lib/serverFailure) is reported to
 * Sentry, and the standard sentence follows the count of those not added,
 * with the support address as a link.
 *
 * @module TrayUploader
 */

import React from 'react';
import { ImagePlus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FailureText } from '@/shared/components/ServerFailure';
import { PhotoDialogShell } from '@/features/plant-list/components/dialogs/photo/PhotoDialogShell';
import { PhotoSourcePicker, type ChosenPicture } from '@/features/plant-list/components/dialogs/photo/PhotoSourcePicker';
import { useCamera } from '@/features/plant-list/components/dialogs/photo/useCamera';
import { useGooglePhotos } from '@/features/plant-list/components/dialogs/photo/useGooglePhotos';
import { useTrayBatch } from '../lib/useTrayBatch';

/** Where the batch is from, and who to tell while it goes in. */
interface TrayUploaderProps {
  /** The bed the batch is from, chosen on the page. */
  locationId: number | null;
  /**
   * True while a batch is going in. The page holds its questions until then:
   * piles are still forming, and a pile asked about half-formed is asked twice.
   */
  onUploadingChange: (uploading: boolean) => void;
}

/**
 * The Choose photos button, its three ways in, and how far a batch has got.
 *
 * @param props - TrayUploaderProps.
 */
export const TrayUploader: React.FC<TrayUploaderProps> = ({ locationId, onUploadingChange }) => {
  const { reactTranslate: t } = useTranslation();
  /** True while the photo source screen is open. */
  const [choosing, setChoosing] = React.useState(false);
  const camera = useCamera();
  // Kept with a Google Photos pick, so its photos still reach the tray, on this bed, if the screen closes first.
  const google = useGooglePhotos(choosing, { kind: 'tray', locationId });
  // The sending, shared with the add pop-ups' photo row (useTrayBatch, D78).
  const batch = useTrayBatch(locationId, { onUploadingChange });
  const { progress } = batch;

  // The camera goes off with the dialog, whichever way it was closed.
  const { stop: stopCamera } = camera;
  React.useEffect(() => {
    if (!choosing) stopCamera();
  }, [choosing, stopCamera]);

  /**
   * The pictures chosen: the dialog closes and they are sent.
   *
   * @param chosen - The pictures chosen, from whichever source.
   */
  const handlePictures = async (chosen: ChosenPicture[]) => {
    setChoosing(false);
    await batch.send(chosen);
  };

  const busy = batch.busy;

  return (
    <div className="flex shrink-0 flex-col">
      <div className="flex items-center gap-3">
        <Button type="button" disabled={busy} onClick={() => setChoosing(true)} className="h-12 px-3">
          {busy ? <Spinner size={16} color="currentColor" /> : <ImagePlus className="h-4 w-4" aria-hidden="true" />}
          {/* The count rides in the button, so the row keeps its shape while a batch goes in. */}
          <span className="ml-2">{busy && progress ? t('tray.uploading', { done: progress.done, total: progress.total }) : t('tray.choose')}</span>
        </Button>
      </div>
      {progress && !busy && progress.failed > 0 && (
        <p className="mt-1 text-xs text-destructive" aria-live="polite">
          {t('tray.uploadFailed', { count: progress.failed })}
          {progress.ours && <> <FailureText text={progress.ours} /></>}
          {progress.refused && <> {progress.refused}</>}
        </p>
      )}

      <PhotoDialogShell isOpen={choosing} onClose={() => setChoosing(false)} title={t('tray.addTitle')}>
        <PhotoSourcePicker
          camera={camera}
          onCaptured={(file) => void handlePictures([{ file, takenAt: new Date() }])}
          onFileChosen={(file, takenAt, googleItemId) => void handlePictures([{ file, takenAt, googleItemId }])}
          onFilesChosen={(pictures) => void handlePictures(pictures)}
          onPickGoogle={google.pick}
          isPickingGoogle={google.isPicking}
          googleStatus={google.status}
          googleCount={google.count}
          onCancelGoogle={google.cancel}
          googleError={google.error}
        />
      </PhotoDialogShell>
    </div>
  );
};
